import { hasFeatureCanary, PlatformDependencies } from '@citrite/workspace-ui-platform';
import { v4 } from 'uuid';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { container } from 'Workspace/DependencyManagement';

export const WSUI_LOGGED_IN_EVENT = 'workspace/cvad/html5/wsuiLoggedIn' as const;
export const WSUI_OFFLINE_EVENT = 'workspace/cvad/html5/wsuiOffline' as const;
const FETCH_ICA_REQUEST = 'workspace/cvad/html5/fetchIca' as const;
const FETCH_ICA_RESPONSE = 'workspace/cvad/html5/fetchIcaAck' as const;

type WorkspaceStatus = typeof WSUI_LOGGED_IN_EVENT | typeof WSUI_OFFLINE_EVENT;

interface BasePayload {
	correlationId: string;
}

interface FetchIcaRequestPayload extends BasePayload {
	resourceId: string;
	launchId: number;
	[key: string]: any;
}

interface FetchIcaResponsePayload extends BasePayload {
	icaData: object;
	launchId: number;
	error?: string | null;
}

interface WSUILoggedInPayload extends BasePayload {}

interface WSUIOfflinePayload extends BasePayload {}

type EventPayloads = {
	[WSUI_LOGGED_IN_EVENT]: WSUILoggedInPayload;
	[WSUI_OFFLINE_EVENT]: WSUIOfflinePayload;
	[FETCH_ICA_REQUEST]: FetchIcaRequestPayload;
	[FETCH_ICA_RESPONSE]: FetchIcaResponsePayload;
};

type BridgeEvent<T extends keyof EventPayloads> = {
	header: {
		type: T;
	};
	payload: EventPayloads[T] & {
		correlationId: string;
	};
};

type FetchIcaRequestEvent = BridgeEvent<typeof FETCH_ICA_REQUEST>;

export type FetchIcaHandler = (request: FetchIcaRequestEvent['payload']) => Promise<void>;

export interface FetchIcaParams {
	icaData: object | null;
	launchId: number | null;
	correlationId: string | null;
	error?: string | null;
}
/**
 * HTML5StatusBridge - Handles communication between workspace-ui and HTML5 client.
 * Supports two scenarios:
 * 1. When workspace-ui is loaded in an iframe
 * 2. When workspace-ui is opened in a new tab with window.opener present
 *
 * This is independent of the launch flow and provides persistent status updates.
 *
 * Use cases:
 * - Notify HTML5 client when user is logged in
 * - Send offline mode status update to HTML5 client for shield scenarios
 * - Handle fetchIca requests from HTML5 client
 * - Report errors or other state changes
 *
 * Note: For iframe scenarios, X-FRAME-OPTIONS headers can affect functionality:
 * - X-FRAME-OPTIONS: DENY will block iframe loading completely
 * - X-FRAME-OPTIONS: SAMEORIGIN allows iframe loading only if parent and child are from same origin
 */
export class HTML5IframeBridge {
	private static instance: HTML5IframeBridge | null = null;
	private parentWindow: Window | null = null;
	private messageListener: ((event: MessageEvent) => void) | null = null;
	private fetchIcaHandler: FetchIcaHandler | null = null;
	private isListenerInitialized = false;

	private constructor() {
		const isInIframe = window.self !== window.top;
		const isNewTabWithOpener = !!window.opener;

		if (!isInIframe && !isNewTabWithOpener) {
			throw new Error(
				'HTML5IframeBridge can only be instantiated inside an iframe or in a new tab with window.opener'
			);
		}

		// Set parent window - either iframe parent or opener window
		this.parentWindow = isInIframe ? window.parent : window.opener;
	}

	/**
	 * Type guard for fetchIca requests
	 */
	private isValidFetchIcaRequest(data: any): data is FetchIcaRequestEvent {
		return (
			data?.header?.type === FETCH_ICA_REQUEST &&
			typeof data?.payload?.correlationId === 'string'
		);
	}

	/**
	 * Get singleton instance of the status bridge
	 */
	public static getInstance(): HTML5IframeBridge | null {
		const workspaceConfiguration = container
			.resolve(PlatformDependencies.WorkspaceConfiguration)
			.get();
		const isSessionReconnectEnabledForHTML5 = hasFeatureCanary(
			workspaceConfiguration,
			FeatureFlag.EnableSessionReconnectForHTML5
		);
		if (!isSessionReconnectEnabledForHTML5) {
			return null;
		}

		// Only create instance if in iframe or new tab with opener
		const isInIframe = window.self !== window.top;
		const isNewTabWithOpener = !!window.opener;

		if (!isInIframe && !isNewTabWithOpener) {
			return null;
		}

		if (!HTML5IframeBridge.instance) {
			HTML5IframeBridge.instance = new HTML5IframeBridge();
		}
		return HTML5IframeBridge.instance;
	}

	/**
	 * Initialize the bridge and start listening for messages from parent
	 */
	public initialize(): void {
		if (this.isListenerInitialized) {
			return;
		}
		this.messageListener = (event: MessageEvent) => {
			// Verify message is from parent window
			if (event.source !== this.parentWindow) {
				return;
			}

			// Verify origin (same domain only)
			if (event.origin !== location.origin) {
				return;
			}

			// Handle incoming messages from HTML5 client
			this.handleParentMessage(event);
		};

		window.addEventListener('message', this.messageListener);
		this.isListenerInitialized = true;
	}

	/**
	 * Handle messages received from HTML5 client parent
	 */
	private async handleParentMessage(event: MessageEvent): Promise<void> {
		const { data } = event;

		// Handle fetchIca request
		if (this.isValidFetchIcaRequest(data)) {
			await this.handleFetchIcaRequest(data);
		}
	}

	/**
	 * Handle fetchIca request from HTML5 client
	 */
	private async handleFetchIcaRequest(request: FetchIcaRequestEvent): Promise<void> {
		if (!this.fetchIcaHandler) {
			return;
		}
		try {
			await this.fetchIcaHandler(request.payload);
			this.destroy();
		} catch (error) {
			this.destroy();
		}
	}

	/**
	 * Send fetchIca response back to HTML5 client
	 */
	public sendFetchIcaResponse(fetchIcaParams: FetchIcaParams): void {
		this.sendEvent(FETCH_ICA_RESPONSE, {
			icaData: fetchIcaParams.icaData,
			launchId: fetchIcaParams.launchId,
			correlationId: fetchIcaParams.correlationId,
			error: fetchIcaParams.error,
		});
	}

	/**
	 * Generic method to send any event to HTML5 client parent window
	 */
	private sendEvent<T extends keyof EventPayloads>(
		eventType: T,
		additionalPayload?: EventPayloads[T]
	): void {
		const event: BridgeEvent<T> = {
			header: {
				type: eventType,
			},
			payload: {
				...additionalPayload,
				correlationId: additionalPayload?.correlationId || v4(),
			},
		};

		this.parentWindow.postMessage(event, location.origin);
	}

	/**
	 * Register a handler for fetchIca requests from HTML5 client
	 */
	public registerFetchIcaHandler(handler: FetchIcaHandler): void {
		this.fetchIcaHandler = handler;
	}

	/**
	 * Send status update to HTML5 client parent window
	 */
	public sendStatusUpdateToHTML5Client(status: WorkspaceStatus): void {
		this.sendEvent(status);
	}

	/**
	 * Cleanup and stop listening
	 */
	public destroy(): void {
		if (this.messageListener) {
			window.removeEventListener('message', this.messageListener);
			this.messageListener = null;
			this.isListenerInitialized = false;
		}
		this.fetchIcaHandler = null;
	}

	public getParentWindow(): Window | null {
		return this.parentWindow;
	}
}
